import { Check, CircleAlert, Copy, ListPlus, MessageSquareText, Music2, RefreshCw, Search, Shuffle, Sparkles, Trophy, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  loadWam,
  loadWamResultDetail,
  loadWamResults,
  runWamAction,
  type WamResult,
  type WamResultDetail,
  type WamResultsStatus,
  type WamSnapshot,
  type WamSuggestion,
} from './wam-api';
import './wam.css';

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';
}

const FIXED_ROUNDS = [
  'Round 1 - Anything goes!',
  'Last Round - Anything you like!',
] as const;

function resultTone(state: WamResult['result_state']): string {
  if (state === 'complete') return 'good';
  if (state === 'failed') return 'bad';
  return 'warning';
}

export function WamPage() {
  const [wam, setWam] = useState<WamSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [manualCategory, setManualCategory] = useState('');
  const [manualAddToList, setManualAddToList] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState<'pending' | 'used' | 'rejected' | 'all'>('pending');
  const [suggestionSort, setSuggestionSort] = useState<'votes' | 'newest'>('votes');
  const [resultsStatus, setResultsStatus] = useState<WamResultsStatus | null>(null);
  const [results, setResults] = useState<WamResult[]>([]);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsSearchDraft, setResultsSearchDraft] = useState('');
  const [resultsSearch, setResultsSearch] = useState('');
  const [resultsState, setResultsState] = useState<'all' | WamResult['result_state']>('all');
  const [selectedResultId, setSelectedResultId] = useState('');
  const [resultDetail, setResultDetail] = useState<WamResultDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);

  const refreshWam = useCallback(async () => {
    try {
      setWam(await loadWam());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'WhatsAMusic could not be loaded.');
    }
  }, []);

  const refreshResults = useCallback(async () => {
    try {
      const next = await loadWamResults(resultsSearch, resultsState);
      setResultsStatus(next.status);
      setResults(next.results);
      setResultsTotal(next.total);
      if (selectedResultId && !next.results.some((item) => item.id === selectedResultId)) {
        setSelectedResultId('');
        setResultDetail(null);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'WAM game history could not be loaded.');
    }
  }, [resultsSearch, resultsState, selectedResultId]);

  useEffect(() => {
    void refreshWam();
    const timer = window.setInterval(() => void refreshWam(), 5000);
    return () => window.clearInterval(timer);
  }, [refreshWam]);

  useEffect(() => {
    void refreshResults();
  }, [refreshResults]);

  useEffect(() => {
    if (!selectedResultId) {
      setResultDetail(null);
      return;
    }
    let cancelled = false;
    setDetailBusy(true);
    void loadWamResultDetail(selectedResultId)
      .then((detail) => { if (!cancelled) setResultDetail(detail); })
      .catch((error: unknown) => {
        if (!cancelled) {
          setResultDetail(null);
          setMessage(error instanceof Error ? error.message : 'WAM game detail could not be loaded.');
        }
      })
      .finally(() => { if (!cancelled) setDetailBusy(false); });
    return () => { cancelled = true; };
  }, [selectedResultId]);

  const ready = wam?.agentConnected === true && wam.service.status === 'online';
  const currentCategory = wam?.service.current?.category?.trim() ?? '';
  const pendingSuggestions = wam?.service.suggestions.filter((item) => item.status === 'pending') ?? [];
  const visibleSuggestions = useMemo(() => {
    return (wam?.service.suggestions ?? [])
      .filter((item) => suggestionFilter === 'all' || item.status === suggestionFilter)
      .slice()
      .sort((left, right) => {
        if (suggestionSort === 'votes') {
          const votes = right.voteCount - left.voteCount;
          if (votes !== 0) return votes;
        }
        return new Date(right.submittedAt ?? 0).getTime() - new Date(left.submittedAt ?? 0).getTime();
      });
  }, [suggestionFilter, suggestionSort, wam]);

  async function action(name: string, payload: Record<string, unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      const response = await runWamAction(name, payload);
      setWam(response.snapshot);
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'WhatsAMusic action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function copyCurrentCategory() {
    if (!currentCategory) return;
    try {
      await navigator.clipboard.writeText(currentCategory);
      setMessage(`Copied “${currentCategory}”.`);
    } catch {
      setMessage('Current WAM category could not be copied.');
    }
  }

  async function manual(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const category = manualCategory.trim();
    if (!category) return;
    await action('manual', { category, addToList: manualAddToList, source: 'ffs-control' }, `Revealed “${category}”.`);
    setManualCategory('');
    setManualAddToList(false);
  }

  async function fixedRound(category: typeof FIXED_ROUNDS[number]) {
    await action(
      'manual',
      { category, addToList: false, source: 'ffs-control-fixed-round' },
      `Revealed “${category}”.`,
    );
  }

  async function editSuggestion(suggestion: WamSuggestion) {
    const text = window.prompt('Edit viewer suggestion. Votes will reset if the text changes.', suggestion.text)?.trim();
    if (!text || text === suggestion.text) return;
    await action('suggestions.edit', { id: suggestion.id, text }, `Updated suggestion #${suggestion.number}.`);
  }

  async function refreshAll() {
    setBusy(true);
    setMessage('');
    try {
      await Promise.all([refreshWam(), refreshResults()]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ffs-workspace-page ffs-wam-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div><small>FRIDAY INTERACTIVE</small><h2>WhatsAMusic</h2><p>Choose the next category, manage viewer suggestions and inspect genuine game results.</p></div>
        <div className="ffs-wam-hero-actions">
          <span className={`ffs-status ${ready ? 'good' : wam?.service.terminalFailure ? 'bad' : 'warning'}`}>{ready ? 'READY' : wam?.agentConnected ? 'CHECK WAM' : 'HOME BOT OFFLINE'}</span>
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refreshAll()}><RefreshCw size={15} /> Refresh</button>
        </div>
      </section>

      <section className="ffs-wam-metrics">
        <Metric label="WAM Agent" value={wam?.agentName ?? 'Not connected'} detail={wam?.service.status ?? 'offline'} />
        <Metric label="Categories" value={(wam?.service.categoryCount ?? 0).toLocaleString('en-GB')} detail="live production list" />
        <Metric label="Weekend remaining" value={wam?.service.weekendRemaining === null || wam?.service.weekendRemaining === undefined ? '—' : wam.service.weekendRemaining.toLocaleString('en-GB')} detail="before repeats" />
        <Metric label="Pending ideas" value={pendingSuggestions.length.toLocaleString('en-GB')} detail={wam?.service.suggestionsOpen ? `${wam.service.suggestionMode} · open` : 'closed'} />
        <Metric label="Room code" value={resultsStatus?.current.roomCode ?? '—'} detail={resultsStatus?.current.roomCode ? 'current WAM room' : 'not reported'} />
      </section>

      <section className="ffs-wam-live-grid">
        <article className="ffs-workspace-card ffs-wam-pick-card">
          <header><div><small>LIVE CATEGORY</small><h3>Next category</h3></div><Music2 size={18} /></header>
          <div className={`ffs-wam-current ${wam?.service.current?.visual === 'cah' ? 'cah' : ''}`}>
            <span>Current category</span><strong>{wam?.service.current?.category ?? 'Nothing selected yet'}</strong>
            <small>{wam?.service.current ? `${wam.service.current.source} · ${formatWhen(wam.service.current.timestamp)}` : 'Selections use the live no-repeat history.'}</small>
            {wam?.service.current?.prompt ? <p>{wam.service.current.prompt}</p> : null}
          </div>
          <div className="ffs-wam-fixed-rounds">
            <span>Every-stream rounds</span>
            <div>
              {FIXED_ROUNDS.map((category) => (
                <button
                  type="button"
                  className="ffs-wam-fixed-round"
                  key={category}
                  disabled={!ready || busy}
                  onClick={() => void fixedRound(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          <div className="ffs-wam-primary-actions">
            <button type="button" className="ffs-button primary" disabled={!ready || busy} onClick={() => void action('random', { source: 'ffs-control' }, 'Random WAM category revealed.')}><Shuffle size={18} /> Random Theme</button>
            <button type="button" className="ffs-button secondary" disabled={!ready || busy} onClick={() => void action('cah', { source: 'ffs-control' }, 'Cards Against Humanity prompt revealed.')}><Sparkles size={18} /> CAH Prompt</button>
          </div>
          <div className="ffs-wam-primary-actions">
            <button type="button" className="ffs-button secondary" disabled={!currentCategory} onClick={() => void copyCurrentCategory()}><Copy size={16} /> Copy Category</button>
            <button type="button" className="ffs-button secondary" disabled={!ready || busy || !currentCategory} onClick={() => void action('category.add', {}, 'Current WAM category added to permanent list.')}><ListPlus size={16} /> Add to Permanent List</button>
          </div>
          <form className="ffs-wam-manual" onSubmit={(event) => void manual(event)}>
            <label><span>Manual category</span><input value={manualCategory} maxLength={180} placeholder="Type the exact category" onChange={(event) => setManualCategory(event.target.value)} /></label>
            <label className="check"><input type="checkbox" checked={manualAddToList} onChange={(event) => setManualAddToList(event.target.checked)} /> Add permanently to category list</label>
            <button type="submit" className="ffs-button secondary" disabled={!ready || busy || !manualCategory.trim()}><Check size={15} /> Reveal</button>
          </form>
          {wam?.service.lastError ? <div className="ffs-wam-warning"><CircleAlert size={16} /><span>{wam.service.lastError}</span></div> : null}
        </article>

        <article className="ffs-workspace-card ffs-wam-history-card">
          <header><div><small>RECENT PICKS</small><h3>Category history</h3></div><span>{wam?.service.recentHistory.length ?? 0}</span></header>
          <div className="ffs-wam-pick-history">
            {(wam?.service.recentHistory ?? []).length === 0 ? <div className="ffs-empty-state">No recent category history reported.</div> : wam?.service.recentHistory.map((item) => (
              <div key={`${item.id}-${item.timestamp}`}><span>{new Date(item.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span><strong>{item.category}</strong><small>{item.source}</small></div>
            ))}
          </div>
        </article>
      </section>

      <section className="ffs-workspace-card ffs-wam-suggestions-card">
        <header><div><small>VIEWER IDEAS</small><h3>Suggestions &amp; voting</h3></div><MessageSquareText size={18} /></header>
        <div className="ffs-wam-suggestion-controls">
          <label><span>Viewer limit</span><select value={wam?.service.suggestionMode ?? 'single'} disabled={!ready || busy} onChange={(event) => void action('suggestions.mode', { mode: event.target.value }, event.target.value === 'single' ? 'One active suggestion per viewer.' : 'Unlimited viewer suggestions enabled.')}><option value="single">One each</option><option value="unlimited">Unlimited</option></select></label>
          <button type="button" className={`ffs-button ${wam?.service.suggestionsOpen ? 'primary' : 'secondary'}`} disabled={!ready || busy} onClick={() => void action(wam?.service.suggestionsOpen ? 'suggestions.close' : 'suggestions.open', {}, wam?.service.suggestionsOpen ? 'Suggestions closed.' : 'Suggestions opened.')}>{wam?.service.suggestionsOpen ? 'Suggestions Open' : 'Open Suggestions'}</button>
          <button
            type="button"
            className={wam?.suggestionChatAcknowledgementsEnabled === false ? 'ffs-button secondary' : 'ffs-button primary'}
            aria-pressed={wam?.suggestionChatAcknowledgementsEnabled !== false}
            disabled={busy}
            title="Mute bot replies to !suggest. Suggestions remain open and visible."
            onClick={() => void action(
              'suggestions.chat-notifications',
              { enabled: wam?.suggestionChatAcknowledgementsEnabled === false },
              wam?.suggestionChatAcknowledgementsEnabled === false
                ? 'Suggestion confirmations enabled.'
                : 'Suggestion confirmations muted; submissions still accepted.',
            )}
          >Suggestion Chat: {wam?.suggestionChatAcknowledgementsEnabled === false ? 'Off' : 'On'}</button>
          <button type="button" className={`ffs-button ${wam?.service.votingOpen ? 'primary' : 'secondary'}`} disabled={!ready || busy} onClick={() => void action(wam?.service.votingOpen ? 'suggestions.voting.close' : 'suggestions.voting.open', {}, wam?.service.votingOpen ? 'Voting closed.' : 'Voting opened.')}>Voting {wam?.service.votingOpen ? 'On' : 'Off'}</button>
          <button type="button" className="ffs-button secondary" disabled={!ready || busy || pendingSuggestions.length === 0} onClick={() => void action('suggestions.reveal', {}, 'Random viewer suggestion revealed.')}>Pick Random</button>
          <button type="button" className="ffs-button secondary" disabled={!ready || busy || pendingSuggestions.length === 0} onClick={() => void action('suggestions.reveal', { highestVoted: true }, 'Highest-voted suggestion revealed.')}>Pick Highest</button>
          <button type="button" className="ffs-button warning" disabled={!ready || busy || pendingSuggestions.length === 0} onClick={() => void action('suggestions.clear', {}, 'Pending suggestions cleared.')}>Clear Pending</button>
        </div>
        <div className="ffs-wam-suggestion-filter">
          <label><span>Show</span><select value={suggestionFilter} onChange={(event) => setSuggestionFilter(event.target.value as typeof suggestionFilter)}><option value="pending">Pending</option><option value="used">Used</option><option value="rejected">Rejected</option><option value="all">All</option></select></label>
          <label><span>Sort</span><select value={suggestionSort} onChange={(event) => setSuggestionSort(event.target.value as typeof suggestionSort)}><option value="votes">Most voted</option><option value="newest">Newest</option></select></label>
          <small>Chat commands: !themes · !vote number · !unvote number</small>
        </div>
        <div className="ffs-wam-suggestion-list">
          {visibleSuggestions.length === 0 ? <div className="ffs-empty-state">No {suggestionFilter === 'all' ? '' : suggestionFilter + ' '}viewer suggestions.</div> : visibleSuggestions.map((suggestion) => (
            <div className={`ffs-wam-suggestion-row ${suggestion.status}`} key={suggestion.id}>
              <strong className="number">#{suggestion.number}</strong>
              <div><strong>{suggestion.text}</strong><small>By {suggestion.user} · {suggestion.voteCount} vote{suggestion.voteCount === 1 ? '' : 's'} · {suggestion.status}</small></div>
              <div className="actions">
                {suggestion.status === 'pending' ? <>
                  <button type="button" disabled={!ready || busy} onClick={() => void action('suggestions.reveal', { id: suggestion.id }, `Revealed “${suggestion.text}”.`)}>Use</button>
                  <button type="button" disabled={!ready || busy} onClick={() => void editSuggestion(suggestion)}>Edit</button>
                  <button type="button" disabled={!ready || busy} onClick={() => void action('suggestions.reject', { id: suggestion.id }, `Rejected “${suggestion.text}”.`)}>Reject</button>
                </> : <button type="button" disabled={!ready || busy} onClick={() => void action('suggestions.restore', { id: suggestion.id }, `Restored “${suggestion.text}”.`)}>Restore</button>}
                <button type="button" className="danger" disabled={!ready || busy} onClick={() => window.confirm(`Delete suggestion #${suggestion.number}?`) && void action('suggestions.delete', { id: suggestion.id }, `Deleted “${suggestion.text}”.`)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="ffs-workspace-card ffs-wam-results-card">
        <header><div><small>GAMES</small><h3>Game results</h3></div><Trophy size={18} /></header>
        <div className={`ffs-wam-result-source ${resultsStatus?.source.liveIngestionEnabled ? 'good' : 'neutral'}`}>
          <Music2 size={17} /><div><strong>{resultsStatus?.source.liveIngestionEnabled ? 'Game result capture enabled' : 'Game result capture disabled'}</strong><span>{resultsStatus?.current.roomCode ? `Room ${resultsStatus.current.roomCode}` : 'No fresh WAM room report'} · {resultsStatus?.current.streamSessionId ? 'stream session linked' : 'no active stream session'}</span></div>
        </div>
        <div className="ffs-wam-results-summary"><Metric label="Stored games" value={resultsTotal.toLocaleString('en-GB')} detail="genuine result records" /><Metric label="Latest result" value={resultsStatus?.latestResult?.result_state ?? '—'} detail={resultsStatus?.latestResult ? formatWhen(resultsStatus.latestResult.occurred_at) : 'No result yet'} /><Metric label="Current room" value={resultsStatus?.current.roomCode ?? '—'} detail="reported WAM room" /></div>
        <form className="ffs-wam-results-filter" onSubmit={(event) => { event.preventDefault(); setResultsSearch(resultsSearchDraft.trim()); }}>
          <label><Search size={15} /><input value={resultsSearchDraft} maxLength={200} placeholder="Search category, room, game or player" onChange={(event) => setResultsSearchDraft(event.target.value)} /></label>
          <select aria-label="Result state" value={resultsState} onChange={(event) => setResultsState(event.target.value as typeof resultsState)}><option value="all">All states</option><option value="complete">Complete</option><option value="partial">Starting / partial</option><option value="failed">Failed</option></select>
          <button type="submit" className="ffs-button secondary">Search</button>
          {(resultsSearch || resultsState !== 'all') ? <button type="button" className="ffs-button secondary" onClick={() => { setResultsSearchDraft(''); setResultsSearch(''); setResultsState('all'); }}>Clear</button> : null}
        </form>
        <div className="ffs-wam-results-layout">
          <div className="ffs-wam-results-list">
            {results.length === 0 ? <div className="ffs-empty-state">No genuine WAM game results match this filter.</div> : results.map((result) => {
              const winner = result.players.find((player) => player.winner) ?? result.players.find((player) => player.placement === 1);
              return <button type="button" className={selectedResultId === result.id ? 'selected' : ''} key={result.id} onClick={() => setSelectedResultId(result.id)}>
                <span className={`state ${resultTone(result.result_state)}`}>{result.result_state}</span><div><strong>{result.category ?? 'Category not supplied'}</strong><small>{formatWhen(result.completed_at ?? result.occurred_at)} · {result.room_code ? `Room ${result.room_code}` : 'Room unknown'}</small></div><span><Trophy size={14} /> {winner?.sourceName ?? winner?.source_name ?? 'Winner unknown'}</span><span><Users size={14} /> {result.player_count}</span>
              </button>;
            })}
          </div>
          <article className="ffs-wam-result-detail">
            {!selectedResultId ? <div className="ffs-empty-state">Select a game to inspect placements and rounds.</div> : detailBusy ? <div className="ffs-empty-state">Loading game details…</div> : resultDetail ? <>
              <div className="title"><div><span className={`state ${resultTone(resultDetail.result.result_state)}`}>{resultDetail.result.result_state}</span><h4>{resultDetail.result.category ?? 'Category not supplied'}</h4></div><small>{formatWhen(resultDetail.result.completed_at ?? resultDetail.result.occurred_at)}</small></div>
              <h5>Final placements</h5><div className="placements">{resultDetail.players.length === 0 ? <div className="ffs-empty-state">No player data supplied.</div> : resultDetail.players.map((player) => <div className={player.winner || player.placement === 1 ? 'winner' : ''} key={player.id}><span>{player.placement ? `#${player.placement}` : '—'}</span><strong>{player.source_name}</strong><em>{player.score ?? '—'} pts</em><small>{player.twitch_user_id ? 'Verified Twitch identity' : 'WAM display name only'}</small></div>)}</div>
              <h5>Rounds</h5><div className="rounds">{resultDetail.rounds.length === 0 ? <div className="ffs-empty-state">No round-level data supplied.</div> : resultDetail.rounds.map((round) => <div key={round.id}><span>#{round.round_number}</span><strong>{round.song_title ?? 'Song title unavailable'}</strong><small>{round.artist_name ?? 'Artist unavailable'}{round.chosen_by_name ? ` · chosen by ${round.chosen_by_name}` : ''}</small></div>)}</div>
            </> : <div className="ffs-empty-state">Game detail could not be loaded.</div>}
          </article>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <article className="ffs-wam-metric"><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}
